import { useCallback, useEffect, useMemo, useState } from 'react'
import type { DrawNumberCap } from '@nexbit/types'
import { apiDrawNumberCaps } from '@/lib/api'

const REFRESH_MS = 5000

/**
 * The house's per-number ceilings for one lottery, kept fresh.
 *
 * Loaded once for the whole page rather than by the panel that edits them, because two
 * things read the same list: the panel, and the stake table above it — which has to
 * measure each number against ITS ceiling. A table that read the bet type's default would
 * show a number the house raised to 20,000 as 100% full at 5,000.
 *
 * `index` is keyed `betType|number`, the key the bet path uses.
 */
export function useNumberCaps(lotteryId: string | null) {
  const [caps, setCaps] = useState<DrawNumberCap[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')

  const reload = useCallback(async () => {
    if (!lotteryId) return
    try {
      setCaps(await apiDrawNumberCaps(lotteryId))
      setState('ready')
    } catch {
      // A failed poll must not blank a list that was already showing — only a first load
      // that never succeeded is an error.
      setState((s) => (s === 'ready' ? s : 'error'))
    }
  }, [lotteryId])

  useEffect(() => {
    setState('loading')
    setCaps([])
    void reload()
    const id = setInterval(() => void reload(), REFRESH_MS)
    return () => clearInterval(id)
  }, [reload])

  const index = useMemo(
    () => new Map(caps.map((c) => [`${c.betType}|${c.number}`, Number(c.maxPerNumber)])),
    [caps],
  )

  return { caps, state, index, reload }
}
